import type { EntityCardField } from '../components/entities/EntityCard';

type FieldExtractor = (entity: any) => EntityCardField[];

function resolveRef(val: any): string {
    if (!val) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') return val.DisplayName || val.Name || val.Id || '';
    return String(val);
}

function raw(entity: any, key: string): any {
    return entity?.rawData?.[key] ?? entity?.[key] ?? null;
}

const LOCALITY_FIELDS: FieldExtractor = (entity) => {
    const address = resolveRef(raw(entity, 'Address')) || resolveRef(raw(entity, 'StreetAddress'));
    const city = resolveRef(raw(entity, 'City'));
    const state = resolveRef(raw(entity, 'State'));
    const zip = resolveRef(raw(entity, 'ZipCode')) || resolveRef(raw(entity, 'PostalCode'));
    const parts = [city, state].filter(Boolean);
    const locationLine = parts.length > 0 ? parts.join(', ') + (zip ? ` ${zip}` : '') : '';

    const fields: EntityCardField[] = [];
    if (address) fields.push({ label: 'Address', value: address, variant: 'text' });
    if (locationLine) fields.push({ label: 'Location', value: locationLine, variant: 'text' });
    return fields;
};

const CARD_FIELD_REGISTRY: Record<string, FieldExtractor> = {
    building: LOCALITY_FIELDS,
    school: (entity) => {
        const base = LOCALITY_FIELDS(entity);
        const schoolType = resolveRef(raw(entity, 'SchoolType'));
        if (schoolType) base.unshift({ label: 'Type', value: schoolType, variant: 'badge', color: 'blue' });
        return base;
    },
    responsibilitycenter: (entity) => {
        const code = resolveRef(raw(entity, 'Code')) || resolveRef(raw(entity, 'RcCode'));
        const parent = resolveRef(raw(entity, 'Parent'));
        const fields: EntityCardField[] = [];
        if (code) fields.push({ label: 'Code', value: code, variant: 'badge', color: 'teal' });
        if (parent) fields.push({ label: 'Parent', value: parent, variant: 'text' });
        return fields;
    },
    class: (entity) => {
        const course = resolveRef(raw(entity, 'Course')) || resolveRef(raw(entity, 'CourseCode'));
        const school = resolveRef(raw(entity, 'School'));
        const teacher = resolveRef(raw(entity, 'Teacher'));
        const fields: EntityCardField[] = [];
        if (course) fields.push({ label: 'Course', value: course, variant: 'badge', color: 'indigo' });
        if (school) fields.push({ label: 'School', value: school, variant: 'text' });
        if (teacher) fields.push({ label: 'Teacher', value: teacher, variant: 'text' });
        return fields;
    },
    studentbusstop: (entity) => {
        const base = LOCALITY_FIELDS(entity);
        const route = resolveRef(raw(entity, 'Route')) || resolveRef(raw(entity, 'BusRoute'));
        if (route) base.unshift({ label: 'Route', value: route, variant: 'badge', color: 'orange' });
        return base;
    },

    // Admin entities
    workflow: (entity) => {
        const type = resolveRef(raw(entity, 'Type'));
        const fields: EntityCardField[] = [];
        if (type) {
            const colorMap: Record<string, string> = { action: 'orange', authorization: 'blue', request: 'violet' };
            fields.push({ label: 'Type', value: type, variant: 'badge', color: colorMap[type.toLowerCase()] || 'gray' });
        }
        return fields;
    },
    workflowactivity: (entity) => {
        const flags: string[] = [];
        if (raw(entity, 'Action')) flags.push('Action');
        if (raw(entity, 'Authorization')) flags.push('Authorization');
        if (raw(entity, 'Request')) flags.push('Request');
        return flags.map(f => ({ label: f, value: f, variant: 'badge' as const, color: f === 'Action' ? 'orange' : f === 'Authorization' ? 'blue' : 'violet' }));
    },
    set: (entity) => {
        const type = resolveRef(raw(entity, 'Type'));
        const isSystem = raw(entity, 'IsSystem');
        const fields: EntityCardField[] = [];
        if (type) {
            const colorMap: Record<string, string> = { manual: 'blue', enumerated: 'teal' };
            fields.push({ label: 'Type', value: type, variant: 'badge', color: colorMap[type.toLowerCase()] || 'gray' });
        }
        if (isSystem) fields.push({ label: 'System', value: 'System', variant: 'badge', color: 'red' });
        return fields;
    },
    policyrule: (entity) => {
        const disabled = raw(entity, 'Disabled');
        const isSystem = raw(entity, 'IsSystem');
        const fields: EntityCardField[] = [];
        if (disabled) fields.push({ label: 'Status', value: 'Disabled', variant: 'badge', color: 'red' });
        if (isSystem) fields.push({ label: 'System', value: 'System', variant: 'badge', color: 'gray' });
        return fields;
    },
    emailtemplate: (entity) => {
        const subject = resolveRef(raw(entity, 'Subject'));
        const contentType = resolveRef(raw(entity, 'ContentType'));
        const fields: EntityCardField[] = [];
        if (subject) fields.push({ label: 'Subject', value: subject, variant: 'text' });
        if (contentType) fields.push({ label: 'Format', value: contentType, variant: 'badge', color: contentType.toLowerCase().includes('html') ? 'blue' : 'gray' });
        return fields;
    },
    requestapprovaltype: (entity) => {
        const type = resolveRef(raw(entity, 'Type'));
        const duration = raw(entity, 'DurationDays');
        const fields: EntityCardField[] = [];
        if (type) {
            fields.push({ label: 'Type', value: type, variant: 'badge', color: type.toLowerCase() === 'possessive' ? 'violet' : 'cyan' });
        }
        if (duration) fields.push({ label: 'Duration', value: `${duration} days`, variant: 'text' });
        return fields;
    },
    settransition: (entity) => {
        const direction = resolveRef(raw(entity, 'Direction'));
        const target = resolveRef(raw(entity, 'TargetSet'));
        const fields: EntityCardField[] = [];
        if (direction) {
            fields.push({ label: 'Direction', value: direction, variant: 'badge', color: direction.toLowerCase() === 'in' ? 'green' : 'orange' });
        }
        if (target) fields.push({ label: 'Target', value: target, variant: 'text' });
        return fields;
    },
    journalentry: (entity) => {
        const status = resolveRef(raw(entity, 'Status'));
        const source = resolveRef(raw(entity, 'Source'));
        const fields: EntityCardField[] = [];
        if (status) {
            const colorMap: Record<string, string> = { completed: 'green', new: 'blue', pending: 'yellow', denied: 'red', expired: 'gray' };
            fields.push({ label: 'Status', value: status, variant: 'badge', color: colorMap[status.toLowerCase()] || 'gray' });
        }
        if (source) fields.push({ label: 'Source', value: source, variant: 'badge', color: 'cyan' });
        return fields;
    },
    requestapproval: (entity) => {
        const status = resolveRef(raw(entity, 'Status'));
        const type = resolveRef(raw(entity, 'Type'));
        const fields: EntityCardField[] = [];
        if (status) {
            const colorMap: Record<string, string> = { pending: 'orange', approved: 'green', denied: 'red', expired: 'gray' };
            fields.push({ label: 'Status', value: status, variant: 'badge', color: colorMap[status.toLowerCase()] || 'gray' });
        }
        if (type) fields.push({ label: 'Type', value: type, variant: 'badge', color: type.toLowerCase() === 'possessive' ? 'violet' : 'cyan' });
        return fields;
    },
    spooledemail: (entity) => {
        const status = resolveRef(raw(entity, 'Status'));
        const to = resolveRef(raw(entity, 'To'));
        const fields: EntityCardField[] = [];
        if (status) {
            fields.push({ label: 'Status', value: status, variant: 'badge', color: status.toLowerCase() === 'sent' ? 'green' : 'yellow' });
        }
        if (to) fields.push({ label: 'To', value: to, variant: 'text' });
        return fields;
    },
    content: (entity) => {
        const subject = resolveRef(raw(entity, 'Subject'));
        const contentType = resolveRef(raw(entity, 'ContentType'));
        const fields: EntityCardField[] = [];
        if (subject) fields.push({ label: 'Subject', value: subject, variant: 'text' });
        if (contentType) fields.push({ label: 'Type', value: contentType, variant: 'badge', color: 'blue' });
        return fields;
    },
    file: (entity) => {
        const fileName = resolveRef(raw(entity, 'FileName'));
        const contentType = resolveRef(raw(entity, 'ContentType'));
        const fields: EntityCardField[] = [];
        if (fileName) fields.push({ label: 'File', value: fileName, variant: 'text' });
        if (contentType) {
            const ct = contentType.toLowerCase();
            const color = ct.includes('image') ? 'blue' : ct.includes('text') ? 'yellow' : 'gray';
            fields.push({ label: 'Type', value: contentType, variant: 'badge', color });
        }
        return fields;
    },
    schemaentity: (entity) => {
        const isEntitySet = raw(entity, 'IsEntitySet');
        const fields: EntityCardField[] = [];
        if (isEntitySet) fields.push({ label: 'Set', value: 'Entity Set', variant: 'badge', color: 'blue' });
        return fields;
    },
    schemaattribute: (entity) => {
        const type = resolveRef(raw(entity, 'Type'));
        const fields: EntityCardField[] = [];
        if (type) {
            const colorMap: Record<string, string> = { string: 'blue', boolean: 'orange', integer: 'teal', datetime: 'grape', binary: 'pink', reference: 'violet', json: 'yellow', decimal: 'cyan' };
            fields.push({ label: 'Type', value: type, variant: 'badge', color: colorMap[type.toLowerCase()] || 'gray' });
        }
        return fields;
    },
    group: (entity) => {
        const managedType = resolveRef(raw(entity, 'ManagedType'));
        const type = resolveRef(raw(entity, 'Type'));
        const fields: EntityCardField[] = [];
        if (managedType) {
            const colorMap: Record<string, string> = { manual: 'blue', enumerated: 'teal', external: 'orange' };
            fields.push({ label: 'Managed', value: managedType, variant: 'badge', color: colorMap[managedType.toLowerCase()] || 'gray' });
        }
        if (type) {
            fields.push({ label: 'Type', value: type, variant: 'badge', color: type.toLowerCase() === 'security' ? 'red' : 'blue' });
        }
        return fields;
    },
    modificationerror: (entity) => {
        const errorType = resolveRef(raw(entity, 'ErrorType')) || resolveRef(raw(entity, 'Type'));
        const target = resolveRef(raw(entity, 'Target')) || resolveRef(raw(entity, 'TargetEntity'));
        const fields: EntityCardField[] = [];
        if (errorType) fields.push({ label: 'Error', value: errorType, variant: 'badge', color: 'red' });
        if (target) fields.push({ label: 'Target', value: target, variant: 'text' });
        return fields;
    },
    appregistration: (entity) => {
        const appId = resolveRef(raw(entity, 'ApplicationId')) || resolveRef(raw(entity, 'AppId'));
        const fields: EntityCardField[] = [];
        if (appId) fields.push({ label: 'App ID', value: appId, variant: 'mono' });
        return fields;
    },
    credential: (entity) => {
        const credType = resolveRef(raw(entity, 'CredentialType')) || resolveRef(raw(entity, 'Type'));
        const target = resolveRef(raw(entity, 'Target'));
        const fields: EntityCardField[] = [];
        if (credType) fields.push({ label: 'Type', value: credType, variant: 'badge', color: 'violet' });
        if (target) fields.push({ label: 'Target', value: target, variant: 'text' });
        return fields;
    },
};

export function getEntityCardFields(entityType: string): FieldExtractor | undefined {
    return CARD_FIELD_REGISTRY[entityType.toLowerCase()];
}

export function getEntityCardFieldsForEntity(entityType: string, entity: any): EntityCardField[] {
    const extractor = CARD_FIELD_REGISTRY[entityType.toLowerCase()];
    return extractor ? extractor(entity) : [];
}
